import { useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import BackButton from '../components/BackButton.jsx';
import LogOutreachModal from '../components/LogOutreachModal.jsx';
import api from '../api/client.js';
import clsx from 'clsx';

const TOUCH_ICON = { visit: '🚗', call: '📞', email: '✉️', quote: '💲', note: '✏️' };
const URGENCY_COLOR = { high: 'text-crimson font-semibold', medium: 'text-gold font-medium', low: 'text-muted' };

const TOUCH_ICON_FD = { visit: '🚗', call: '📞', email: '✉️', quote: '💲', note: '✏️', photo: '📷' };

export default function FollowupDetail() {
  const { id } = useParams();
  const qc = useQueryClient();
  const navigate = useNavigate();
  const [loggingOutreach, setLoggingOutreach] = useState(false);

  const q = useQuery({
    queryKey: ['followup', id],
    queryFn: () => api.get(`/followups/${id}`).then(r => r.data.data),
  });

  const update = useMutation({
    mutationFn: (body) => api.put(`/followups/${id}`, body).then(r => r.data.data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['followup', id] });
      qc.invalidateQueries({ queryKey: ['followups'] });
    },
  });

  if (q.isLoading) return <div className="text-muted">Loading…</div>;
  if (q.isError) return <div className="text-crimson">Failed to load follow-up — {q.error?.response?.data?.error || 'unknown error'}</div>;

  const f = q.data;
  const isPending = ['pending', 'snoozed'].includes(f.status);
  const isOverdue = isPending && new Date(f.due_date) < new Date();
  const daysOverdue = isOverdue
    ? Math.floor((Date.now() - new Date(f.due_date).getTime()) / 86400000)
    : null;

  return (
    <div className="space-y-4 max-w-2xl">
      <header>
        <div className="section-eyebrow">
          <Link to="/followups" className="hover:underline">Follow-ups</Link> · Detail
        </div>
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <h1 className="text-2xl">{f.title}</h1>
          <BackButton fallback="/followups" />
        </div>
        <div className="rule-crimson" />
      </header>

      <section className="card p-4 space-y-4">
        <div className="grid grid-cols-2 gap-3 text-sm">
          <Field label="Status">
            <span className={clsx('capitalize font-medium',
              f.status === 'done' ? 'text-emerald-700'
              : isOverdue ? 'text-crimson'
              : 'text-navy')}>
              {f.status}
              {daysOverdue != null && ` · ${daysOverdue}d overdue`}
            </span>
          </Field>
          <Field label="Due">{new Date(f.due_date).toLocaleDateString()}</Field>
          <Field label="Urgency">
            <span className={clsx('capitalize', URGENCY_COLOR[f.urgency] || 'text-navy')}>{f.urgency}</span>
          </Field>
          {f.touch_type && <Field label="Type"><span className="capitalize">{f.touch_type}</span></Field>}
          <Field label="Customer">
            {f.customer_id
              ? <span>
                  <Link to={`/customers/${f.customer_id}`} className="text-crimson hover:underline">{f.customer_name}</Link>
                  {' '}<span className="text-[10px] px-1 py-0.5 rounded bg-emerald-100 text-emerald-800">🔗 linked</span>
                </span>
              : (f.customer_name_hint || f.source_customer_name)
                ? <span>{f.customer_name_hint || f.source_customer_name} <span className="text-[10px] px-1 py-0.5 rounded bg-amber-100 text-amber-800">unlinked</span></span>
                : <span className="italic text-muted">General task</span>}
          </Field>
          <Field label="Rep">{f.user_name || '—'}</Field>
          {f.completed_at && <Field label="Completed">{new Date(f.completed_at).toLocaleDateString()}</Field>}
        </div>

        {f.description && (
          <div className="text-sm">
            <div className="text-[10px] uppercase tracking-wider text-muted mb-1">Notes</div>
            <p className="text-navy whitespace-pre-wrap">{f.description}</p>
          </div>
        )}

        <div className="flex gap-2 pt-1 border-t border-navy-100 flex-wrap">
          {isPending && (
            <>
              <button
                className="btn-primary text-sm"
                disabled={update.isPending}
                onClick={() => update.mutate({ status: 'done' })}
              >
                {update.isPending ? 'Saving…' : '✓ Mark done'}
              </button>
              <SnoozeMenu onSnooze={(due_date) => update.mutate({ status: 'snoozed', due_date })} disabled={update.isPending} />
            </>
          )}
          <button
            className="btn-ghost text-sm"
            onClick={() => navigate(`/notes/new?follow_up_id=${id}${f.customer_id ? `&customer_id=${f.customer_id}` : ''}`)}
          >
            ✏️ Post note
          </button>
          {isPending && f.customer_id && (
            <button className="btn-ghost text-sm" onClick={() => setLoggingOutreach(true)}>
              📞 Log outreach
            </button>
          )}
        </div>
      </section>

      {f.source_note_id && (
        <section className="card p-4">
          <div className="section-eyebrow">Originated from</div>
          <div className="rule-crimson" />
          <Link to={`/notes/${f.source_note_id}`}
            className="block text-xs py-1.5 px-2 mt-2 rounded hover:bg-panel flex items-baseline gap-2">
            <span>{TOUCH_ICON_FD[f.source_note_touch_type] || '✏️'}</span>
            <span className="flex-1 truncate text-navy">{f.source_note_summary || '(note)'}</span>
            <span className="text-muted whitespace-nowrap">
              {f.source_note_date ? new Date(f.source_note_date + 'T12:00:00').toLocaleDateString() : ''}
            </span>
          </Link>
        </section>
      )}

      {loggingOutreach && (
        <LogOutreachModal
          customerId={f.customer_id}
          customerName={f.customer_name}
          followUpId={f.id}
          onClose={() => setLoggingOutreach(false)}
          onLogged={() => qc.invalidateQueries({ queryKey: ['followup', id] })}
        />
      )}

      {f.reply_notes?.length > 0 && (
        <section className="card p-4">
          <div className="section-eyebrow">Notes posted for this follow-up</div>
          <div className="rule-crimson" />
          <div className="space-y-1 mt-2">
            {f.reply_notes.map(n => (
              <Link key={n.id} to={`/notes/${n.id}`}
                className="block text-xs py-1.5 px-2 rounded hover:bg-panel flex items-baseline gap-2">
                <span>{TOUCH_ICON_FD[n.touch_type] || '✏️'}</span>
                <span className="flex-1 truncate text-navy">{n.summary || '(note)'}</span>
                <span className="text-muted whitespace-nowrap">
                  {n.note_date ? new Date(n.note_date + 'T12:00:00').toLocaleDateString() : new Date(n.created_at).toLocaleDateString()}
                </span>
              </Link>
            ))}
          </div>
        </section>
      )}

      {f.recent_notes?.length > 0 && (
        <section className="card p-4">
          <div className="section-eyebrow">Recent activity — {f.customer_name}</div>
          <div className="rule-crimson" />
          <div className="space-y-1 mt-2">
            {f.recent_notes.map(n => (
              <Link key={n.id} to={`/notes/${n.id}`}
                className="block text-xs py-1.5 px-2 rounded hover:bg-panel flex items-baseline gap-2">
                <span>{TOUCH_ICON[n.touch_type] || '✏️'}</span>
                <span className="flex-1 truncate text-navy">{n.summary || '(note)'}</span>
                <span className="text-muted whitespace-nowrap">{new Date(n.created_at).toLocaleDateString()}</span>
              </Link>
            ))}
          </div>
        </section>
      )}
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-wider text-muted mb-0.5">{label}</div>
      <div className="text-navy">{children}</div>
    </div>
  );
}

const SNOOZE_PRESETS = [
  { label: '3 hours', ms: 3 * 60 * 60 * 1000 },
  { label: 'Tomorrow', ms: 24 * 60 * 60 * 1000 },
  { label: '3 days', ms: 3 * 24 * 60 * 60 * 1000 },
  { label: '1 week', ms: 7 * 24 * 60 * 60 * 1000 },
];

function SnoozeMenu({ onSnooze, disabled }) {
  return (
    <div className="relative group">
      <button className="btn-ghost text-sm" disabled={disabled}>Snooze ▾</button>
      <div className="absolute left-0 top-full mt-1 bg-white border border-navy-100 rounded shadow-panel z-10 hidden group-focus-within:block group-hover:block min-w-[120px]">
        {SNOOZE_PRESETS.map(p => (
          <button
            key={p.label}
            className="block w-full text-left px-3 py-2 text-sm hover:bg-panel"
            onClick={() => {
              const d = new Date(Date.now() + p.ms);
              onSnooze(d.toISOString());
            }}
          >
            {p.label}
          </button>
        ))}
      </div>
    </div>
  );
}
